/**
 * Media Library - Iter 9.40 Slice I.i (redesigned).
 * 3-tab beautiful grid: Service inspo · Private inspo · Final looks.
 * Each image:
 *   - Linked to product(s) and service(s)
 *   - Eye icon → universal preference card popup if it came from a completed appointment
 *   - Hover: search/add product + service badges
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, Plus, Trash2, Lock, Loader2, Tag as TagIcon, Image as ImageIcon, Sparkles, Search, Upload, X } from "lucide-react";
import UniversalPreferenceCardPopup from "@/components/UniversalPreferenceCardPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "service", label: "Service inspo", icon: Sparkles, hint: "Default inspo your booking page shows for each service." },
  // Iter 9.40 Slice I.j - Final looks merged into Private inspo per user direction.
  { id: "private", label: "Private inspo", icon: Lock, hint: "Per-client inspo only the host sees + final-look uploads from completed appointments." },
];

export default function MediaLibraryPanel({ eventId, category, masterProducts: masterProductsProp = [] }) {
  const [tab, setTab] = useState("service");
  const [items, setItems] = useState({ service: [], private: [] });
  const [loading, setLoading] = useState(true);
  const [masterProducts, setMasterProducts] = useState(masterProductsProp);
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [popup, setPopup] = useState(null);
  const [uploading, setUploading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  useEffect(() => {
    if (masterProductsProp.length) { setMasterProducts(masterProductsProp); return; }
    if (!category) return;
    fetch(`${API}/host/category-bundle?category_id=${encodeURIComponent(category)}&event_id=${encodeURIComponent(eventId || "")}`, { headers: headers() })
      .then(r => r.ok ? r.json() : null).then(d => d && setMasterProducts(d.master_products || [])).catch(() => {});
    fetch(`${API}/host/services?event_id=${encodeURIComponent(eventId || "")}`, { headers: headers() })
      .then(r => r.ok ? r.json() : null).then(d => d && setServices(d.services || d || [])).catch(() => {});
  }, [category, eventId, masterProductsProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    if (!eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Iter 9.40 Slice I.j - final looks fold into private inspo.
      const [svc, priv, fin] = await Promise.all([
        fetch(`${API}/host/service-inspo?event_id=${encodeURIComponent(eventId)}`, { headers: headers() }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
        fetch(`${API}/host/private-inspo?event_id=${encodeURIComponent(eventId)}&category_id=${encodeURIComponent(category || "")}`, { headers: headers() }).then(r => r.ok ? r.json() : { inspo: [] }).catch(() => ({ inspo: [] })),
        fetch(`${API}/host/final-looks?event_id=${encodeURIComponent(eventId)}`, { headers: headers() }).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
      ]);
      const finalItems = (fin.items || fin.final_looks || []).map(x => ({ ...x, kind: "final_look" }));
      const privateItems = priv.inspo || priv.items || [];
      setItems({
        service: svc.items || svc.inspo || [],
        private: [...privateItems, ...finalItems],
      });
    } finally { setLoading(false); }
  }, [eventId, category]);
  useEffect(() => { reload(); }, [reload]);

  const filteredItems = useMemo(() => {
    const list = items[tab] || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(i =>
      (i.linked_product_names || []).some(n => String(n).toLowerCase().includes(q)) ||
      (i.linked_service_names || []).some(n => String(n).toLowerCase().includes(q)) ||
      (i.client_name || "").toLowerCase().includes(q) ||
      (i.notes || "").toLowerCase().includes(q)
    );
  }, [items, tab, search]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("event_id", eventId);
    fd.append("scope", tab); // service | private | final
    try {
      const r = await fetch(`${API}/host/media-library/upload`, { method: "POST", headers: headers(), body: fd });
      if (!r.ok) throw new Error("upload failed");
      toast.success(`Added to ${TABS.find(t => t.id === tab)?.label}`);
      reload();
    } catch { toast.error("Couldn't upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-5" data-testid="media-library-panel">
      <div>
        <h3 className="text-lg font-semibold">Media library</h3>
        <p className="text-sm text-stone-500">{TABS.find(t => t.id === tab)?.hint}</p>
      </div>

      {/* Tab switcher */}
      <div className="inline-flex p-1 bg-stone-100 rounded-xl" data-testid="media-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
              tab === t.id ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-900"
            }`}
            data-testid={`media-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            <span className="text-[10px] text-stone-400 ml-1">{(items[t.id] || []).length}</span>
          </button>
        ))}
      </div>

      {/* Search + upload */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${TABS.find(t => t.id === tab)?.label.toLowerCase()}…`}
            className="pl-9"
            data-testid="media-search"
          />
        </div>
        <label className="cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="media-upload-input" />
          <Button asChild disabled={uploading} variant="outline" size="sm">
            <span><Upload className="w-3.5 h-3.5 mr-1" />{uploading ? "Uploading…" : "Add image"}</span>
          </Button>
        </label>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-stone-200 rounded-2xl">
          <ImageIcon className="w-10 h-10 mx-auto text-stone-300 mb-3" />
          <p className="text-sm text-stone-500">No {TABS.find(t => t.id === tab)?.label.toLowerCase()} yet.</p>
          <p className="text-xs text-stone-400 mt-1">Use "Add image" above to upload your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="media-grid">
          {filteredItems.map((item, idx) => (
            <MediaCard
              key={item.id || item.image_url || idx}
              item={item}
              tab={tab}
              masterProducts={masterProducts}
              services={services}
              onEye={(card) => setPopup(card)}
              onRefresh={reload}
              eventId={eventId}
            />
          ))}
        </div>
      )}

      {/* Universal popup */}
      {popup && (
        <UniversalPreferenceCardPopup
          open={!!popup}
          onOpenChange={(o) => { if (!o) setPopup(null); }}
          client={popup.client}
          inspo={popup.inspo || []}
          tagLabels={popup.tags || []}
          linkedProducts={popup.products || []}
          finalLooks={popup.final_looks || []}
          testIdPrefix="media-popup"
        />
      )}
    </div>
  );
}

function MediaCard({ item, tab, masterProducts, services, onEye, onRefresh, eventId }) {
  const [hover, setHover] = useState(false);
  const url = item.image_url || item.url;
  const productNames = (item.linked_product_names || []).slice(0, 2);
  const serviceNames = (item.linked_service_names || item.service_names || []).slice(0, 2);
  const isFromCompletedBooking = !!(item.rsvp_id || item.booking_id || item.kind === "final_look");

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative group rounded-xl overflow-hidden bg-stone-100 aspect-square shadow-sm hover:shadow-md transition-all"
      data-testid={`media-card-${item.id || item.image_url}`}
    >
      <img src={url} alt="" className="w-full h-full object-cover" />
      {/* Eye-icon - opens universal preference card popup if it came from a booking */}
      {isFromCompletedBooking && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEye({
              client: { name: item.client_name, service: item.service_name, date: item.date },
              inspo: [url, ...(item.related_inspo || [])],
              tags: item.tags || [],
              products: item.linked_products || [],
              final_looks: item.kind === "final_look" ? [url] : (item.final_look_urls || []),
            });
          }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-md"
          data-testid="media-card-eye"
        >
          <Eye className="w-4 h-4 text-stone-700" />
        </button>
      )}
      {/* Hover overlay - linked products + services + add buttons */}
      <div className={`absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/85 via-black/40 to-transparent transition-opacity ${hover ? "opacity-100" : "opacity-0"}`}>
        {productNames.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {productNames.map((n, i) => (
              <Badge key={i} className="bg-white/95 text-stone-800 text-[9px] border-0 font-normal">{n}</Badge>
            ))}
            {item.linked_products?.length > 2 && <Badge className="bg-white/70 text-stone-700 text-[9px] border-0">+{item.linked_products.length - 2}</Badge>}
          </div>
        )}
        {serviceNames.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {serviceNames.map((n, i) => (
              <Badge key={i} className="bg-purple-500/95 text-white text-[9px] border-0 font-normal">{n}</Badge>
            ))}
          </div>
        )}
      </div>
      {/* Bottom-left attach mini-buttons */}
      <div className={`absolute bottom-2 left-2 flex gap-1 transition-opacity ${hover ? "opacity-100" : "opacity-0"}`}>
        <button
          onClick={async (e) => {
            e.stopPropagation();
            const name = window.prompt("Link product (search in Master Products):");
            if (!name) return;
            const found = masterProducts.find(p => (p.name || "").toLowerCase().includes(name.toLowerCase()));
            if (!found) { toast.error("No product matched"); return; }
            await fetch(`${API}/host/media-library/${item.id}/link-product`, {
              method: "POST",
              headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}`, "Content-Type": "application/json" },
              body: JSON.stringify({ product_id: found.product_id || found.id, event_id: eventId }),
            });
            toast.success(`Linked ${found.name}`);
            onRefresh();
          }}
          className="w-7 h-7 rounded-full bg-white/95 backdrop-blur hover:bg-white shadow flex items-center justify-center"
          data-testid="media-card-link-product"
          title="Link product"
        >
          <TagIcon className="w-3 h-3 text-stone-700" />
        </button>
        <button
          onClick={async (e) => {
            e.stopPropagation();
            const name = window.prompt("Link service (search):");
            if (!name) return;
            const found = (services || []).find(s => (s.name || "").toLowerCase().includes(name.toLowerCase()));
            if (!found) { toast.error("No service matched"); return; }
            await fetch(`${API}/host/media-library/${item.id}/link-service`, {
              method: "POST",
              headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}`, "Content-Type": "application/json" },
              body: JSON.stringify({ service_id: found.service_id || found.id, event_id: eventId }),
            });
            toast.success(`Linked ${found.name}`);
            onRefresh();
          }}
          className="w-7 h-7 rounded-full bg-white/95 backdrop-blur hover:bg-white shadow flex items-center justify-center"
          data-testid="media-card-link-service"
          title="Link service"
        >
          <Sparkles className="w-3 h-3 text-stone-700" />
        </button>
      </div>
    </div>
  );
}
